/** A preset value written `@t:some.key` is already translated by the dictionary — never a sentence. */
const TEXT_MARKER = /^@t:[a-z][a-z0-9_]*(?:\.[a-z0-9_]+)+$/;
/**
 * True for a value worth translating: it has at least one letter, is not a bare url/anchor/path, and is
 * not a `@t:` marker. `#`, `/collections/x`, `249 DH`, `` are all skipped.
 */
export function isProse(value) {
    if (typeof value !== 'string')
        return false;
    const v = value.trim();
    if (v === '' || TEXT_MARKER.test(v))
        return false;
    if (/^(https?:\/\/|\/|#|mailto:|tel:)/.test(v))
        return false;
    return /\p{L}/u.test(v);
}
/**
 * Text fields that hold a REFERENCE, not a sentence — never translated.
 *
 * The trap the retro pass found on 2026-09-13: `swatchesOptionName` is typed `text` and reads like a
 * word ("Color"), but it names the VARIANT OPTION whose values are rendered as swatches. Translating it
 * to "Couleur" on a French store silently empties the swatch row, because no product has an option by
 * that name. Same for the facet lists, the hidden-product tag, and the `[value]` template.
 *
 * A brand name (`storeName`) is not a reference but a NAME, and a name does not translate either.
 */
const REFERENCE_FIELDS = new Set([
    'swatchesOptionName',
    'facetsSwatchesOptions',
    'facetsPillsOptions',
    'collectionHiddenTag',
    'activeButtonsText',
    'customBadgesList',
    'daysToSkip',
    'storeName',
    'username',
]);
function fieldIndex(sections, blocks) {
    return {
        section: (type) => sections[type]?.schema.fields,
        block: (sectionType, blockType) => sections[sectionType]?.schema.blocks?.find((b) => b.type === blockType)?.fields ??
            blocks[blockType]?.schema.fields,
    };
}
/** The prose-bearing leaves of one settings object, as (path, value) pairs. */
function settingsTexts(fields, settings, prefix, out, unknown, ownerType) {
    for (const [id, value] of Object.entries(settings)) {
        if (REFERENCE_FIELDS.has(id))
            continue;
        const field = fields?.find((f) => f.id === id);
        if (!field) {
            if (isProse(value) || (value && typeof value === 'object' && 'html' in value)) {
                unknown.add(`${ownerType}.${id}`);
            }
            continue;
        }
        switch (field.type) {
            case 'text':
            case 'textarea':
                if (isProse(value))
                    out.push({ path: `${prefix}/${id}`, value });
                break;
            case 'richtext': {
                const html = value?.html;
                if (isProse(html))
                    out.push({ path: `${prefix}/${id}`, value: html });
                else if (isProse(value))
                    out.push({ path: `${prefix}/${id}`, value });
                break;
            }
            case 'image': {
                const alt = value?.alt;
                if (isProse(alt))
                    out.push({ path: `${prefix}/${id}.alt`, value: alt });
                break;
            }
            case 'link': {
                const label = value?.label;
                if (isProse(label))
                    out.push({ path: `${prefix}/${id}.label`, value: label });
                break;
            }
            default:
                break;
        }
    }
}
function blockTexts(index, sectionType, block, prefix, out, unknown) {
    const path = `${prefix}/${block.id}`;
    settingsTexts(index.block(sectionType, block.type), block.settings, path, out, unknown, block.type);
    for (const child of block.blocks ?? [])
        blockTexts(index, sectionType, child, path, out, unknown);
}
/** Every sentence of one document, in document order. Meta keys are prefixed with the template. */
export function collectDocumentTexts(doc, sections, blocks) {
    const index = fieldIndex(sections, blocks);
    const out = [];
    const unknown = new Set();
    const meta = `${doc.template}:meta`;
    if (isProse(doc.meta?.title))
        out.push({ path: `${meta}/title`, value: doc.meta.title });
    if (isProse(doc.meta?.seo?.metaTitle)) {
        out.push({ path: `${meta}/seo.metaTitle`, value: doc.meta.seo.metaTitle });
    }
    if (isProse(doc.meta?.seo?.metaDescription)) {
        out.push({ path: `${meta}/seo.metaDescription`, value: doc.meta.seo.metaDescription });
    }
    for (const section of doc.sections) {
        settingsTexts(index.section(section.type), section.settings, section.id, out, unknown, section.type);
        for (const block of section.blocks ?? [])
            blockTexts(index, section.type, block, section.id, out, unknown);
    }
    return { entries: out, unknown: [...unknown].sort() };
}
/**
 * The sentences of a whole theme (every template), de-duplicated by path. The same path seen twice with
 * DIFFERENT text is refused: it would mean one dictionary key for two sentences.
 */
export function collectThemeTexts(documents, sections, blocks) {
    const byPath = new Map();
    const unknown = new Set();
    for (const doc of Object.values(documents)) {
        const collected = collectDocumentTexts(doc, sections, blocks);
        collected.unknown.forEach((u) => unknown.add(u));
        for (const { path, value } of collected.entries) {
            const seen = byPath.get(path);
            if (seen !== undefined && seen !== value) {
                throw new Error(`theme-i18n: path "${path}" holds two different sentences ("${seen}" / "${value}") — give the section a unique id.`);
            }
            byPath.set(path, value);
        }
    }
    return {
        entries: [...byPath].map(([path, value]) => ({ path, value })),
        unknown: [...unknown].sort(),
    };
}
/** FNV-1a over the sorted (path, value) pairs — the dictionary's staleness check. */
export function sourceHash(entries) {
    const payload = [...entries]
        .sort((a, b) => (a.path < b.path ? -1 : a.path > b.path ? 1 : 0))
        .map((e) => `${e.path}=${e.value}`)
        .join('\n');
    let h = 0x811c9dc5;
    for (let i = 0; i < payload.length; i++) {
        h ^= payload.charCodeAt(i);
        h = Math.imul(h, 0x01000193) >>> 0;
    }
    return `${h.toString(16)}-${entries.length.toString(36)}`;
}
// ─────────────────────────────────────────────────────────────────────────────
// Writing sentences back
// ─────────────────────────────────────────────────────────────────────────────
function setLeaf(settings, id, sub, text) {
    const current = settings[id];
    if (sub === undefined) {
        if (typeof current === 'string') {
            settings[id] = text;
            return true;
        }
        if (current && typeof current === 'object' && 'html' in current) {
            settings[id] = {
                ...current,
                html: text,
            };
            return true;
        }
        return false;
    }
    if (current && typeof current === 'object' && sub in current) {
        settings[id] = { ...current, [sub]: text };
        return true;
    }
    return false;
}
function findBlock(blocks, ids) {
    let list = blocks;
    let found;
    for (const id of ids) {
        found = list?.find((b) => b.id === id);
        if (!found)
            return undefined;
        list = found.blocks;
    }
    return found;
}
/**
 * Apply `entries` (path → sentence) to a document IN PLACE, for the paths the document actually has.
 * Returns the number of sentences written. Entries for other templates (`index:meta/title`) and for
 * missing sections are skipped, never an error — the header document only holds the home's chrome.
 */
export function applyTexts(doc, entries) {
    let written = 0;
    for (const [key, text] of Object.entries(entries)) {
        const metaMatch = /^([a-z]+):meta\/(.+)$/.exec(key);
        if (metaMatch) {
            if (metaMatch[1] !== doc.template || !doc.meta)
                continue;
            const target = metaMatch[2];
            if (target === 'title' && typeof doc.meta.title === 'string') {
                doc.meta.title = text;
                written++;
            }
            else if (target.startsWith('seo.') && doc.meta.seo) {
                const k = target.slice('seo.'.length);
                if (typeof doc.meta.seo[k] === 'string') {
                    doc.meta.seo[k] = text;
                    written++;
                }
            }
            continue;
        }
        const parts = key.split('/');
        const leaf = parts.pop();
        const dot = leaf.indexOf('.');
        const fieldId = dot === -1 ? leaf : leaf.slice(0, dot);
        const sub = dot === -1 ? undefined : leaf.slice(dot + 1);
        const section = doc.sections.find((s) => s.id === parts[0]);
        if (!section)
            continue;
        const holder = parts.length === 1 ? section : findBlock(section.blocks, parts.slice(1));
        if (!holder)
            continue;
        if (setLeaf(holder.settings, fieldId, sub, text))
            written++;
    }
    return written;
}
//# sourceMappingURL=paths.js.map